import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PrintingTrendsChart = ({ projects = [], filters }) => {
  const chartData = useMemo(() => {
    if (!projects?.length) return [];

    // Agrupar por mês dentro do intervalo
    const start = filters?.startDate ? new Date(filters?.startDate) : null;
    const end = filters?.endDate ? new Date(filters?.endDate) : null;
    if (end) end.setHours(23, 59, 59, 999);

    const fmtKey = (d) => {
      const date = new Date(d);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    };
    const fmtLabel = (key) => {
      const [y, m] = key.split('-');
      const dt = new Date(Number(y), Number(m) - 1, 1);
      return dt.toLocaleDateString('pt-BR', { month: 'short' });
    };

    const map = new Map();
    projects.forEach(p => {
      const created = p?.created_at ? new Date(p?.created_at) : null;
      if (!created) return;
      if (start && created < start) return;
      if (end && created > end) return;
      const key = fmtKey(created);
      const cur = map.get(key) || { receita: 0, horas: 0 };
      cur.receita += Number(p?.final_price || 0);
      cur.horas += Number(p?.estimated_duration_hours || 0);
      map.set(key, cur);
    });

    // Ordenar por mês
    const keys = Array.from(map.keys()).sort();
    return keys.map(k => ({ month: fmtLabel(k), receita: map.get(k)?.receita || 0, horas: map.get(k)?.horas || 0 }));
  }, [projects, filters]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.dataKey === 'receita' ? 'Receita' : 'Horas'}: {' '}
              {entry?.dataKey === 'receita' 
                ? `R$ ${entry?.value?.toFixed(2)}` 
                : `${entry?.value}h`
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Tendências de Impressão</h2>
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-muted-foreground">Receita</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-secondary" />
            <span className="text-muted-foreground">Horas</span>
          </div>
        </div>
      </div>

      <div className="h-64 w-full" aria-label="Gráfico de tendências de impressão mensal">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="month" 
              stroke="#94a3b8"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              yAxisId="receita"
              orientation="left"
              stroke="#94a3b8"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `R$ ${value}`}
            />
            <YAxis 
              yAxisId="horas"
              orientation="right"
              stroke="#94a3b8"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}h`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              yAxisId="receita"
              type="monotone" 
              dataKey="receita" 
              stroke="#00d4ff" 
              strokeWidth={3}
              dot={{ fill: '#00d4ff', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#00d4ff', strokeWidth: 2 }}
            />
            <Line 
              yAxisId="horas"
              type="monotone" 
              dataKey="horas" 
              stroke="#0ea5e9" 
              strokeWidth={3}
              dot={{ fill: '#0ea5e9', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#0ea5e9', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PrintingTrendsChart;